import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart } from 'lightweight-charts';
import s from './TradingDashboard.module.sass';
import binance from '../../img/binance-coin-bnb-logo-CD94CC6D31-seeklogo.com.png';
import bitcoin from '../../img/Bitcoin.svg.png';
import ethereum from '../../img/Ethereum-ETH-icon.png';
import usdclogo from '../../img/usdclogo.png';
import solana from '../../img/solana.png';
import { useSimpleTranslation } from '../../hooks/useTranslation';
import { notifySuccess } from '../../utils/notify';

export const TradingDashboard = ({ maxTotalMoney, isNavbarCollapsed, currencyFormat, selectedCrypto, siteSettings }) => {
    const chartContainerRef = useRef();
    const chartRef = useRef();
    const candleSeriesRef = useRef();
    const chartIntervalRef = useRef();
    const isChartMountedRef = useRef(false);
    
    // Система переводов
    const { t } = useSimpleTranslation(siteSettings);
    
    // Функция для получения иконки криптовалюты
    const getCryptoIcon = (crypto) => {
        switch (crypto) {
            case 'BTC': return bitcoin;
            case 'ETH': return ethereum;
            case 'USDC': return usdclogo;
            case 'SOL': return solana;
            case 'BNB':
            default: return binance;
        }
    };
    
    const [orderType, setOrderType] = useState('limit');
    const [side, setSide] = useState('buy');
    const [price, setPrice] = useState('');
    const [amount, setAmount] = useState('');
    
    // Функция для форматирования чисел согласно настройкам
    const formatNumber = (number, format = 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        
        const num = parseFloat(number);
        
        // Принудительное форматирование без зависимости от системной локали
        const parts = num.toFixed(2).split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        
        if (format === 'dot') {
            // Европейский формат: 10.294,98 (точка для тысяч, запятая для десятых)
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            return `${formattedInteger},${decimalPart}`;
        } else {
            // Американский формат: 10,294.98 (запятая для тысяч, точка для десятых)
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return `${formattedInteger}.${decimalPart}`;
        }
    };
    
    // Реальные данные с API
    const [marketData, setMarketData] = useState({
        currentPrice: 0,
        priceChange: 0,
        high24h: 0,
        low24h: 0,
        volume24h: 0,
        marketCap: 0
    });
    
    // Торговая пара всегда с USD (хардкод)
    const [selectedPair, setSelectedPair] = useState(`${selectedCrypto || 'BNB'}/USD`);
    
    // Обновляем selectedPair при изменении selectedCrypto (всегда USD)
    useEffect(() => {
        setSelectedPair(`${selectedCrypto || 'BNB'}/USD`);
    }, [selectedCrypto]);
    
    // Перезагружаем график при смене криптовалюты
    useEffect(() => {
        if (isChartMountedRef.current) {
            // Очищаем старый интервал графика
            if (chartIntervalRef.current) {
                clearInterval(chartIntervalRef.current);
            }
            
            // Сбрасываем marketData чтобы избежать показа данных с предыдущей криптовалюты
            setMarketData({
                currentPrice: 0,
                priceChange: 0,
                high24h: 0,
                low24h: 0,
                volume24h: 0,
                marketCap: 0
            });
            
            // Перезагружаем данные графика с новой криптовалютой
            fetchChartData();
        }
    }, [selectedCrypto]);
    
    // Генерация данных для стакана на основе текущей цены
    const [orderBook, setOrderBook] = useState({
        bids: [],
        asks: []
    });

    // Генерация данных для открытых ордеров
    const [openOrders, setOpenOrders] = useState([]);
    
    // Генерация данных для истории сделок
    const [tradeHistory, setTradeHistory] = useState([]);

    // Состояние для анимации заполнения ордеров
    const [fillingOrders, setFillingOrders] = useState(new Set());
    
    // Состояние для новых сделок (для анимации)
    const [newTrades, setNewTrades] = useState(new Set());

    // Очистка графика
    const cleanupChart = useCallback(() => {
        if (chartIntervalRef.current) {
            clearInterval(chartIntervalRef.current);
            chartIntervalRef.current = null;
        }
        
        if (candleSeriesRef.current) {
            candleSeriesRef.current = null;
        }
        
        if (chartRef.current) {
            try {
                chartRef.current.remove();
            } catch (e) {
                console.warn('Chart cleanup warning:', e);
            }
            chartRef.current = null;
        }
        
        isChartMountedRef.current = false;
    }, []);

    // Инициализация графика с правильной очисткой
    const initializeChart = useCallback(() => {
        if (!chartContainerRef.current || isChartMountedRef.current) return;
        
        // Определяем текущую тему
        const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
        const isDark = currentTheme === 'dark';
        const isLiquidGlass = currentTheme === 'liquid-glass';
        const isSolana = currentTheme === 'solana';
        const isBinance = currentTheme === 'binance';
        
        try {
            const chart = createChart(chartContainerRef.current, {
                width: chartContainerRef.current.clientWidth,
                height: 600,
                layout: {
                    background: {
                        color: isLiquidGlass ? 'transparent' : (isSolana || isBinance ? 'transparent' : (isDark ? '#1e293b' : '#ffffff')),
                    },
                    textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                },
                grid: {
                    vertLines: {
                        color: isSolana ? 'rgba(50, 200, 255, 0.1)' : isBinance ? 'rgba(240, 185, 11, 0.1)' : (isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(197, 203, 206, 0.3)'),
                    },
                    horzLines: {
                        color: isSolana ? 'rgba(50, 200, 255, 0.1)' : isBinance ? 'rgba(240, 185, 11, 0.1)' : (isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(197, 203, 206, 0.3)'),
                    },
                },
                crosshair: {
                    mode: 'normal',
                    vertLine: {
                        color: isSolana ? 'rgba(50, 200, 255, 0.5)' : isBinance ? 'rgba(240, 185, 11, 0.5)' : (isDark ? 'rgba(203, 213, 225, 0.5)' : 'rgba(33, 56, 77, 0.5)'),
                        width: 1,
                    },
                    horzLine: {
                        color: isSolana ? 'rgba(50, 200, 255, 0.5)' : isBinance ? 'rgba(240, 185, 11, 0.5)' : (isDark ? 'rgba(203, 213, 225, 0.5)' : 'rgba(33, 56, 77, 0.5)'),
                        width: 1,
                    },
                },
                rightPriceScale: {
                    borderColor: isSolana ? 'rgba(50, 200, 255, 0.2)' : isBinance ? 'rgba(240, 185, 11, 0.2)' : (isDark ? 'rgba(71, 85, 105, 0.8)' : 'rgba(197, 203, 206, 0.8)'),
                    visible: true,
                    textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                },
                timeScale: {
                    borderColor: isSolana ? 'rgba(50, 200, 255, 0.2)' : isBinance ? 'rgba(240, 185, 11, 0.2)' : (isDark ? 'rgba(71, 85, 105, 0.8)' : 'rgba(197, 203, 206, 0.8)'),
                    timeVisible: true,
                    secondsVisible: false,
                    textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                },
                handleScroll: {
                    mouseWheel: true,
                    pressedMouseMove: true,
                },
                handleScale: {
                    axisPressedMouseMove: true,
                    mouseWheel: true,
                    pinch: true,
                },
            });

            const series = chart.addCandlestickSeries({
                upColor: isSolana ? 'rgba(0, 255, 150, 1)' : isBinance ? '#f0b90b' : '#16a085',
                downColor: '#e53e3e',
                borderDownColor: '#e53e3e',
                borderUpColor: isSolana ? 'rgba(0, 255, 150, 1)' : isBinance ? '#f0b90b' : '#16a085',
                wickDownColor: '#e53e3e',
                wickUpColor: isSolana ? 'rgba(0, 255, 150, 1)' : isBinance ? '#f0b90b' : '#16a085',
            });

            chartRef.current = chart;
            candleSeriesRef.current = series;
            isChartMountedRef.current = true;

            // Убеждаемся, что фон установлен правильно сразу после создания
            if (chartRef.current) {
                chartRef.current.applyOptions({
                    layout: {
                        background: {
                            color: isLiquidGlass ? 'transparent' : (isSolana || isBinance ? 'transparent' : (isDark ? '#1e293b' : '#ffffff')),
                        },
                        textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                    },
                });
            }

            // Загружаем данные графика
            fetchChartData();

            // Обработка изменения размера
            const handleResize = () => {
                if (chartContainerRef.current && chartRef.current && isChartMountedRef.current) {
                    try {
                        chartRef.current.applyOptions({
                            width: chartContainerRef.current.clientWidth,
                        });
                    } catch (e) {
                        console.warn('Chart resize warning:', e);
                    }
                }
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                cleanupChart();
            };
        } catch (error) {
            console.error('Chart initialization error:', error);
            cleanupChart();
        }
    }, []);

    // Функция для загрузки данных графика с безопасной очисткой
    const fetchChartData = async () => {
        if (!candleSeriesRef.current || !isChartMountedRef.current) return;
        
        try {
            const symbol = `${selectedCrypto || 'BNB'}USDT`;
            const response = await fetch(`/api/binance/klines?symbol=${symbol}&interval=1h&limit=500`);
            const data = await response.json();

            // Устанавливаем все свечи, кроме последней
            const cdata = data.slice(0, -1).map(d => ({
                time: d[0] / 1000,
                open: parseFloat(d[1]),
                high: parseFloat(d[2]),
                low: parseFloat(d[3]),
                close: parseFloat(d[4]),
            }));

            if (candleSeriesRef.current && isChartMountedRef.current) {
                candleSeriesRef.current.setData(cdata);

                // Для USDC показываем обычный график без манипуляций
                if (selectedCrypto === 'USDC') {
                    // Просто добавляем последнюю свечу как есть
                    const lastData = data[data.length - 1];
                    const lastCandle = {
                        time: lastData[0] / 1000,
                        open: parseFloat(lastData[1]),
                        high: parseFloat(lastData[2]),
                        low: parseFloat(lastData[3]),
                        close: parseFloat(lastData[4]),
                    };
                    candleSeriesRef.current.update(lastCandle);
                    
                    // Сохраняем реальную цену для других компонентов
                    setMarketData(prev => ({
                        ...prev,
                        currentPrice: parseFloat(lastData[4])
                    }));
                } else {
                    // Трюк с последней свечой: показываем от +0.8% до +1.3%
                    const lastData = data[data.length - 1];
                const open = parseFloat(lastData[1]);
                const realClose = parseFloat(lastData[4]);
                const realHigh = parseFloat(lastData[2]);
                const realLow = parseFloat(lastData[3]);
                
                // Устанавливаем close от +0.8% до +1.3% выше реальной цены
                const minIncrease = realClose * 1.008; // +0.8%
                const maxIncrease = realClose * 1.013; // +1.3%
                let fakeClose = minIncrease + (maxIncrease - minIncrease) * Math.random();

                // Инициализируем тени - они будут накапливать максимумы и минимумы
                let shadowHigh = Math.max(open, fakeClose, realHigh);
                let shadowLow = Math.min(open, fakeClose, realLow);

                // Устанавливаем начальную последнюю свечу
                const lastCandle = {
                    time: lastData[0] / 1000,
                    open: open,
                    high: shadowHigh,
                    low: shadowLow,
                    close: fakeClose,
                };

                if (candleSeriesRef.current && isChartMountedRef.current) {
                    candleSeriesRef.current.update(lastCandle);

                    // Сохраняем текущую цену для других компонентов
                    setMarketData(prev => ({
                        ...prev,
                        currentPrice: fakeClose
                    }));

                    // Колебания последней свечи в диапазоне +0.8% до +1.3%
                    if (chartIntervalRef.current) {
                        clearInterval(chartIntervalRef.current);
                    }
                    
                    chartIntervalRef.current = setInterval(() => {
                        if (!candleSeriesRef.current || !isChartMountedRef.current) {
                            clearInterval(chartIntervalRef.current);
                            return;
                        }
                        
                        const newFakeClose = minIncrease + (maxIncrease - minIncrease) * Math.random();
                        
                        // Обновляем тени только если цена действительно зашла туда
                        if (newFakeClose > shadowHigh) {
                            shadowHigh = newFakeClose;
                        }
                        if (newFakeClose < shadowLow) {
                            shadowLow = newFakeClose;
                        }
                        
                        try {
                            if (candleSeriesRef.current && isChartMountedRef.current) {
                                candleSeriesRef.current.update({
                                    time: lastCandle.time,
                                    open: lastCandle.open,
                                    high: shadowHigh,
                                    low: shadowLow,
                                    close: newFakeClose,
                                });

                                // Обновляем текущую цену для синхронизации
                                setMarketData(prev => ({
                                    ...prev,
                                    currentPrice: newFakeClose
                                }));
                            }
                        } catch (e) {
                            console.warn('Chart update warning:', e);
                            clearInterval(chartIntervalRef.current);
                        }
                    }, 2000);
                }
                } // Закрытие else блока для манипуляции свечи
            }
        } catch (error) {
            console.error('Ошибка загрузки данных графика:', error);
        }
    };

    // Убрали функцию анимации изменения значений

    // Обновление реальных данных с Binance API
    useEffect(() => {
    const fetchMarketData = async () => {
        try {
                const symbol = `${selectedCrypto || 'BNB'}USDT`;
                const [priceResponse, statsResponse] = await Promise.all([
                    fetch(`/api/binance/ticker/price?symbol=${symbol}`),
                    fetch(`/api/binance/ticker/24hr?symbol=${symbol}`)
                ]);

                const priceData = await priceResponse.json();
                const statsData = await statsResponse.json();
            
                const newMarketData = {
                    currentPrice: parseFloat(priceData.price),
                    priceChange: parseFloat(statsData.priceChange),
                    high24h: parseFloat(statsData.highPrice),
                    low24h: parseFloat(statsData.lowPrice),
                    volume24h: parseFloat(statsData.volume),
                    marketCap: 95.2 // Статическое значение для примера
                };

                // Просто обновляем данные без анимации
                setMarketData(newMarketData);
        } catch (error) {
                console.error('Ошибка при получении данных с Binance:', error);
            }
        };

        fetchMarketData();
        const interval = setInterval(fetchMarketData, 5000);

        return () => clearInterval(interval);
    }, [selectedCrypto]); // Перезапускаем при смене криптовалюты

    // Генерация открытых ордеров на основе реальной цены
    const generateOpenOrders = (basePrice) => {
        const orders = [];
        for (let i = 0; i < 4; i++) {
            const orderPrice = basePrice + (Math.random() - 0.5) * 20;
            orders.push({
                id: `ORD${Date.now()}${i}`,
                type: Math.random() > 0.5 ? 'Buy' : 'Sell',
                pair: selectedPair,
                price: orderPrice.toFixed(2),
                amount: (Math.random() * 10 + 1).toFixed(4),
                filled: Math.random() * 100,
                time: new Date(Date.now() - Math.random() * 3600000).toLocaleTimeString()
            });
        }
        setOpenOrders(orders);
    };

    // Генерация истории сделок на основе реальной цены
    const generateTradeHistory = (basePrice) => {
        const trades = [];
        const now = Date.now();
        
        // Используем РЕАЛЬНУЮ цену с графика
        const realPrice = marketData.currentPrice || basePrice;
        
        // Генерируем 10 строк для начальной истории (добавил 2 как просили: было 8, стало 10)
        for (let i = 0; i < 10; i++) {
            const priceVariation = (Math.random() - 0.5) * 0.02 * realPrice; // ±2%
            const tradePrice = realPrice + priceVariation;
            const side = Math.random() > 0.5 ? 'buy' : 'sell';
            
            // ИСПРАВЛЯЮ: время назад всего на несколько секунд, а не минут!
            const secondsAgo = (i + 1) * (5 + Math.random() * 10); // 5-15 секунд за каждую сделку
            const tradeTime = new Date(now - secondsAgo * 1000);
            
            trades.push({
                id: `initial_trade_${i}`,
                time: tradeTime.toLocaleTimeString('en-US', { hour12: true }),
                price: tradePrice.toFixed(2),
                amount: (Math.random() * 5 + 0.1).toFixed(4),
                side: side,
                isNew: false,
                timestamp: tradeTime.getTime()
            });
        }
        
        // Сортируем по времени (новые сверху)
        trades.sort((a, b) => b.timestamp - a.timestamp);
        setTradeHistory(trades);
    };

    // Инициализация графика
    useEffect(() => {
        const cleanup = initializeChart();
        return cleanup;
    }, [initializeChart, siteSettings?.theme]);

    // Обновление темы графика при изменении темы
    useEffect(() => {
        if (!chartRef.current || !isChartMountedRef.current) return;
        
        const updateChartTheme = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
            const isDark = currentTheme === 'dark';
            const isLiquidGlass = currentTheme === 'liquid-glass';
            const isSolana = currentTheme === 'solana';
            const isBinance = currentTheme === 'binance';
            
            try {
                chartRef.current.applyOptions({
                    layout: {
                        background: {
                            color: isLiquidGlass ? 'transparent' : (isSolana || isBinance ? 'transparent' : (isDark ? '#1e293b' : '#ffffff')),
                        },
                        textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                    },
                    grid: {
                        vertLines: {
                            color: isSolana ? 'rgba(50, 200, 255, 0.1)' : isBinance ? 'rgba(240, 185, 11, 0.1)' : (isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(197, 203, 206, 0.3)'),
                        },
                        horzLines: {
                            color: isSolana ? 'rgba(50, 200, 255, 0.1)' : isBinance ? 'rgba(240, 185, 11, 0.1)' : (isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(197, 203, 206, 0.3)'),
                        },
                    },
                    crosshair: {
                        vertLine: {
                            color: isSolana ? 'rgba(50, 200, 255, 0.5)' : isBinance ? 'rgba(240, 185, 11, 0.5)' : (isDark ? 'rgba(203, 213, 225, 0.5)' : 'rgba(33, 56, 77, 0.5)'),
                            width: 1,
                        },
                        horzLine: {
                            color: isSolana ? 'rgba(50, 200, 255, 0.5)' : isBinance ? 'rgba(240, 185, 11, 0.5)' : (isDark ? 'rgba(203, 213, 225, 0.5)' : 'rgba(33, 56, 77, 0.5)'),
                            width: 1,
                        },
                    },
                    rightPriceScale: {
                        borderColor: isSolana ? 'rgba(50, 200, 255, 0.2)' : isBinance ? 'rgba(240, 185, 11, 0.2)' : (isDark ? 'rgba(71, 85, 105, 0.8)' : 'rgba(197, 203, 206, 0.8)'),
                        textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                    },
                    timeScale: {
                        borderColor: isSolana ? 'rgba(50, 200, 255, 0.2)' : isBinance ? 'rgba(240, 185, 11, 0.2)' : (isDark ? 'rgba(71, 85, 105, 0.8)' : 'rgba(197, 203, 206, 0.8)'),
                        textColor: isSolana || isBinance ? '#e2e8f0' : (isDark ? '#cbd5e1' : 'rgba(33, 56, 77, 1)'),
                    },
                });
                
                // Обновляем цвета свечей для тем Solana и Binance
                if (candleSeriesRef.current) {
                    candleSeriesRef.current.applyOptions({
                        upColor: isSolana ? 'rgba(0, 255, 150, 1)' : isBinance ? '#f0b90b' : '#16a085',
                        borderUpColor: isSolana ? 'rgba(0, 255, 150, 1)' : isBinance ? '#f0b90b' : '#16a085',
                        wickUpColor: isSolana ? 'rgba(0, 255, 150, 1)' : isBinance ? '#f0b90b' : '#16a085',
                    });
                }
            } catch (e) {
                console.warn('Chart theme update warning:', e);
            }
        };
        
        // Обновляем сразу
        updateChartTheme();
        
        // Слушаем изменения data-theme атрибута
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    updateChartTheme();
                }
            });
        });
        
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });
        
        return () => {
            observer.disconnect();
        };
    }, [siteSettings?.theme]);

        // Создаем начальную историю сделок ТОЛЬКО ОДИН РАЗ!
    useEffect(() => {
        generateTradeHistory(621); // базовая цена для начала
    }, []);

    // Обновление данных при изменении цены - НЕ пересоздаем Order Book!
    useEffect(() => {
        if (marketData.currentPrice > 0) {
            // Обновляем только если Order Book пустой (первая инициализация)
            setOrderBook(prev => {
                if (prev.asks.length === 0 || prev.bids.length === 0) {
                    return generateInitialOrderBook(marketData.currentPrice);
                }
                // Если Order Book уже есть - не трогаем его, пусть ордера живут своей жизнью
                return prev;
            });
            
            generateOpenOrders(marketData.currentPrice);
            
            // TradeHistory генерируется ТОЛЬКО при первом запуске!
            // БОЛЬШЕ НИКОГДА НЕ ТРОГАЕМ СТАРЫЕ СДЕЛКИ!
        }
    }, [marketData.currentPrice]);

    // Функция для начальной генерации ордербука (только при старте)
    const generateInitialOrderBook = (basePrice) => {
        const bids = [];
        const asks = [];
        
        // Берем РЕАЛЬНУЮ цену с графика вместо basePrice
        const realPrice = marketData.currentPrice || basePrice;
        
        // Генерируем 6 ордеров для каждой стороны
        for (let i = 0; i < 6; i++) {
            // Используем реальную цену с графика для расчета
            const bidPriceStep = (0.1 + Math.random() * 0.2) * (i + 1);
            const askPriceStep = (0.1 + Math.random() * 0.2) * (i + 1);
            
            const bidPrice = realPrice - bidPriceStep;
            const askPrice = realPrice + askPriceStep;
            
            bids.push({
                id: `bid_${Date.now()}_${i}`,
                price: bidPrice.toFixed(2),
                amount: (Math.random() * 50 + 10).toFixed(4),
                total: (bidPrice * (Math.random() * 50 + 10)).toFixed(2),
                fillProgress: 0,
                filled: false,
                isAnimating: false,
                targetFill: 0,
                animationSpeed: 1,
                fillFrequency: 0.7
            });
            
            asks.push({
                id: `ask_${Date.now()}_${i}`,
                price: askPrice.toFixed(2),
                amount: (Math.random() * 50 + 10).toFixed(4),
                total: (askPrice * (Math.random() * 50 + 10)).toFixed(2),
                fillProgress: 0,
                filled: false,
                isAnimating: false,
                targetFill: 0,
                animationSpeed: 1,
                fillFrequency: 0.7
            });
        }
        
        // Сортируем правильно перед возвратом
        // Asks: сортируем по убыванию цены (самая высокая цена сверху)
        asks.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        // Bids: сортируем по убыванию цены (самая высокая цена сверху)  
        bids.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        
        return { bids, asks };
    };

    // Очистка при размонтировании
    useEffect(() => {
        return () => {
            cleanupChart();
        };
    }, [cleanupChart]);

    // Обновление ордербука в реальном времени с анимацией заполнения (как в TradingDashboard 2)
    useEffect(() => {
        const orderBookUpdateInterval = setInterval(() => {
            setOrderBook(prev => {
                const newOrderBook = { asks: [...prev.asks], bids: [...prev.bids] };
                
                // Обрабатываем asks и bids отдельно для правильного подсчета
                // Обрабатываем asks (ордера на продажу)
                newOrderBook.asks.forEach((order, index) => {
                    // 35% шанс начать анимацию для каждого ордера
                    if (Math.random() < 0.35 && !order.isAnimating) {
                        order.isAnimating = true;
                        order.fillProgress = 0;
                        
                        // Для asks: позиции 4-5 = самые низкие цены = более выгодные для покупателей
                        let advantageLevel;
                        if (index >= 4) {
                            advantageLevel = 'high'; // Выгодные (низкие цены для покупателей)
                        } else if (index >= 2) {
                            advantageLevel = 'medium'; // Средние
                        } else {
                            advantageLevel = 'low'; // Невыгодные (высокие цены)
                        }
                        
                        if (advantageLevel === 'high') {
                            order.animationSpeed = Math.random() * 2 + 4; // Скорость 4-6
                            order.targetFill = Math.random() * 20 + 80; // 80-100% заполнения
                            order.fillFrequency = 0.9; // 90% шанс скачка
                        } else if (advantageLevel === 'medium') {
                            order.animationSpeed = Math.random() * 2 + 2; // Скорость 2-4
                            order.targetFill = Math.random() * 25 + 70; // 70-95% заполнения
                            order.fillFrequency = 0.7; // 70% шанс скачка
                        } else {
                            order.animationSpeed = Math.random() * 1.5 + 0.5; // Скорость 0.5-2
                            order.targetFill = Math.random() * 30 + 60; // 60-90% заполнения
                            order.fillFrequency = 0.4; // 40% шанс скачка
                        }
                        
                        order.animationStartTime = Date.now();
                    }
                });

                // Обрабатываем bids (ордера на покупку)
                newOrderBook.bids.forEach((order, index) => {
                    // 35% шанс начать анимацию для каждого ордера
                    if (Math.random() < 0.35 && !order.isAnimating) {
                        order.isAnimating = true;
                        order.fillProgress = 0;
                        
                        // Для bids: позиции 0-1 = самые высокие цены = более выгодные для продавцов
                        let advantageLevel;
                        if (index <= 1) {
                            advantageLevel = 'high'; // Выгодные (высокие цены для продавцов)
                        } else if (index <= 3) {
                            advantageLevel = 'medium'; // Средние
                        } else {
                            advantageLevel = 'low'; // Невыгодные (низкие цены)
                        }
                        
                        if (advantageLevel === 'high') {
                            order.animationSpeed = Math.random() * 2 + 4; // Скорость 4-6
                            order.targetFill = Math.random() * 20 + 80; // 80-100% заполнения
                            order.fillFrequency = 0.9; // 90% шанс скачка
                        } else if (advantageLevel === 'medium') {
                            order.animationSpeed = Math.random() * 2 + 2; // Скорость 2-4
                            order.targetFill = Math.random() * 25 + 70; // 70-95% заполнения
                            order.fillFrequency = 0.7; // 70% шанс скачка
                        } else {
                            order.animationSpeed = Math.random() * 1.5 + 0.5; // Скорость 0.5-2
                            order.targetFill = Math.random() * 30 + 60; // 60-90% заполнения
                            order.fillFrequency = 0.4; // 40% шанс скачка
                        }
                        
                        order.animationStartTime = Date.now();
                    }
                });
                
                return newOrderBook;
            });
        }, 150); // Проверяем каждые 150мс (как в оригинале)
        
        return () => clearInterval(orderBookUpdateInterval);
    }, []); // Убираю зависимость от цены - ордера должны заполняться независимо!

    // Отдельный useEffect для обновления прогресса анимаций с быстрыми скачками
    useEffect(() => {
        const animationInterval = setInterval(() => {
            setOrderBook(prev => {
                const newOrderBook = { asks: [...prev.asks], bids: [...prev.bids] };
                let hasAnimations = false;
                
                [...newOrderBook.asks, ...newOrderBook.bids].forEach(order => {
                    if (order.isAnimating) {
                        hasAnimations = true;
                        
                        // Используем fillFrequency для определения шанса скачка
                        const shouldJump = Math.random() < (order.fillFrequency || 0.7);
                        
                        if (shouldJump) {
                            // Размер скачка зависит от animationSpeed
                            const baseJumpSize = Math.random() * 15 + 5; // Базовый скачок 5-20%
                            const speedMultiplier = (order.animationSpeed || 2) / 2; // Нормализуем скорость
                            const jumpSize = baseJumpSize * speedMultiplier;
                            
                            order.fillProgress = Math.min(
                                order.fillProgress + jumpSize, 
                                order.targetFill
                            );
                        }
                        
                        // Если анимация достигла цели, завершаем ТОЛЬКО ЭТОТ ордер
                        if (order.fillProgress >= order.targetFill) {
                            // Показываем полное заполнение (100%) на секунду перед заменой
                            order.fillProgress = 100;
                            
                            // Держим на 100% на 0.5-1 секунда, затем заменяем ТОЛЬКО этот ордер
                            setTimeout(() => {
                                setOrderBook(currentOrderBook => {
                                    const resetOrderBook = { 
                                        asks: [...currentOrderBook.asks], 
                                        bids: [...currentOrderBook.bids] 
                                    };
                                    
                                    // Находим тип ордера (ask или bid)
                                    const isAsk = resetOrderBook.asks.find(o => o.id === order.id);
                                    const targetArray = isAsk ? resetOrderBook.asks : resetOrderBook.bids;
                                    const orderIndex = targetArray.findIndex(o => o.id === order.id);
                                    
                                    if (orderIndex !== -1) {
                                        // Создаем новый ордер с РЕАЛЬНОЙ ценой с графика
                                        const realCurrentPrice = marketData.currentPrice || 621;
                                        let newPrice;
                                        
                                        if (isAsk) {
                                            // Для ask - цена выше РЕАЛЬНОЙ цены с графика
                                            // Позиция в стакане определяет насколько выше
                                            const priceStep = (orderIndex + 1) * (0.1 + Math.random() * 0.2);
                                            newPrice = realCurrentPrice + priceStep;
                                        } else {
                                            // Для bid - цена ниже РЕАЛЬНОЙ цены с графика
                                            // Позиция в стакане определяет насколько ниже
                                            const priceStep = (orderIndex + 1) * (0.1 + Math.random() * 0.2);
                                            newPrice = realCurrentPrice - priceStep;
                                        }
                                        
                                        const newAmount = Math.random() * 50 + 10;
                                        
                                        // Заменяем заполненный ордер новым
                                        targetArray[orderIndex] = {
                                            id: `${isAsk ? 'ask' : 'bid'}_${Date.now()}_${Math.random()}`,
                                            price: newPrice.toFixed(2),
                                            amount: newAmount.toFixed(4),
                                            total: (newPrice * newAmount).toFixed(2),
                                            fillProgress: 0,
                                            filled: false,
                                            isAnimating: false,
                                            targetFill: 0,
                                            animationSpeed: 1,
                                            fillFrequency: 0.7
                                        };
                                        
                                        // ВАЖНО: Сортируем Order Book правильно
                                        if (isAsk) {
                                            // Asks: сортируем по убыванию цены (самая высокая цена сверху)
                                            resetOrderBook.asks.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
                                        } else {
                                            // Bids: сортируем по убыванию цены (самая высокая цена сверху)
                                            resetOrderBook.bids.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
                                        }
                                    }
                                    
                                    return resetOrderBook;
                                });
                            }, Math.random() * 300 + 200); // 0.2-0.5 секунды на 100%
                        }
                    }
                });
                
                return hasAnimations ? newOrderBook : prev;
            });
        }, 80); // Обновляем каждые 80мс
        
        return () => clearInterval(animationInterval);
    }, []); // Без зависимостей - ордера заполняются независимо от цены!

    // РАНДОМНЫЙ интервал для реалистичного онлайн обновления сделок
    useEffect(() => {
        let tradesTimeout;
        
        const scheduleNextTrade = () => {
            // Рандомный интервал с УКЛОНОМ в быстрые интервалы
            // 70% шанс быстрых интервалов (200мс-1сек), 30% медленных (1-3сек)
            let randomInterval;
            if (Math.random() < 0.7) {
                // 70% - быстрые интервалы (несколько сделок в секунду)
                randomInterval = Math.random() * 800 + 200; // 200мс - 1000мс
            } else {
                // 30% - медленные интервалы (1 сделка за 1-3 сек)
                randomInterval = Math.random() * 2000 + 1000; // 1000мс - 3000мс
            }
            
            tradesTimeout = setTimeout(() => {
                setTradeHistory(prev => {
                    const priceVariation = (Math.random() - 0.5) * 0.01 * marketData.currentPrice; // ±1%
                    const tradePrice = marketData.currentPrice + priceVariation;
                    const side = Math.random() > 0.5 ? 'buy' : 'sell';
                    
                    // Реалистичные объемы сделок
                    let amount;
                    const rand = Math.random();
                    if (rand > 0.95) {
                        amount = Math.random() * 50 + 20; // Крупные сделки
                    } else if (rand > 0.85) {
                        amount = Math.random() * 20 + 5; // Средние сделки
                    } else if (rand > 0.6) {
                        amount = Math.random() * 5 + 1; // Обычные сделки
                    } else {
                        amount = Math.random() * 2 + 0.1; // Мелкие сделки
                    }
                    
                    // Новая сделка получает ТЕКУЩЕЕ время
                    const newTrade = {
                        id: `trade_${Date.now()}_${Math.random()}`,
                        time: new Date().toLocaleTimeString('en-US', { hour12: true }),
                        price: tradePrice.toFixed(2),
                        amount: amount.toFixed(4),
                        side: side,
                        isNew: true,
                        timestamp: Date.now()
                    };
                    
                    // Добавляем ТОЛЬКО СВЕРХУ, сдвигаем все остальное вниз
                    return [newTrade, ...prev.slice(0, 9)];
                });
                
                // Планируем следующую сделку с новым рандомным интервалом
                scheduleNextTrade();
            }, randomInterval);
        };
        
        // Запускаем первую сделку
        scheduleNextTrade();

        return () => {
            if (tradesTimeout) {
                clearTimeout(tradesTimeout);
            }
        };
    }, [marketData.currentPrice]);

    // УБРАЛ быстрый интервал - он создавал хаос!

    const handlePlaceOrder = () => {
        notifySuccess(`${side.toUpperCase()} order placed: ${amount} ${selectedCrypto || 'BNB'} at $${price}`);
    };

    return (
        <div className={`${s.tradingDashboard} ${isNavbarCollapsed ? s.collapsed : ''}`}>
            {/* Header с реальными данными */}
                <div className={s.header}>
                {/* Основная информация о паре */}
                <div className={s.mainSection}>
                    <div className={s.pairSelector}>
                        <img src={getCryptoIcon(selectedCrypto)} alt={selectedCrypto || 'BNB'} className={s.pairLogo} />
                        <select 
                            className={s.pairDropdown}
                            value={selectedPair}
                            onChange={(e) => setSelectedPair(e.target.value)}
                        >
                            <option value="BNB/USD">BNB/USD</option>
                            <option value="BTC/USD">BTC/USD</option>
                            <option value="ETH/USD">ETH/USD</option>
                            <option value="USDC/USD">USDC/USD</option>
                        </select>
                        <svg className={s.dropdownIcon} width="10" height="6" viewBox="0 0 10 6">
                            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    
                    <div className={s.priceInfo}>
                        <span className={s.currentPrice}>
                            ${formatNumber(marketData.currentPrice, siteSettings?.numberFormat)}
                        </span>
                        <span className={`${s.priceChange} ${marketData.priceChange >= 0 ? s.positive : s.negative}`}>
                            {marketData.priceChange >= 0 ? '+' : ''}{formatNumber(marketData.priceChange, siteSettings?.numberFormat)} ({((marketData.priceChange / marketData.currentPrice) * 100).toFixed(2)}%)
                        </span>
                        </div>
                        </div>

                {/* Статистика */}
                <div className={s.statsSection}>
                    <div className={s.stat}>
                        <span className={s.statLabel}>{t('tradingDashboard.stats.high24h', 'Макс. 24ч')}</span>
                        <span className={`${s.statValue} ${s.positive}`}>
                            ${formatNumber(marketData.high24h, siteSettings?.numberFormat)}
                        </span>
                    </div>
                    <div className={s.stat}>
                        <span className={s.statLabel}>{t('tradingDashboard.stats.low24h', 'Мин. 24ч')}</span>
                        <span className={`${s.statValue} ${s.negative}`}>
                            ${formatNumber(marketData.low24h, siteSettings?.numberFormat)}
                        </span>
                    </div>
                    <div className={s.stat}>
                        <span className={s.statLabel}>{t('tradingDashboard.stats.volume24h', 'Объём 24ч')}</span>
                        <span className={s.statValue}>
                            {formatNumber(marketData.volume24h / 1000, siteSettings?.numberFormat).replace(/\.\d+/, '')}K {selectedCrypto || 'BNB'}
                        </span>
                            </div>
                    <div className={s.stat}>
                        <span className={s.statLabel}>{t('tradingDashboard.stats.marketCap', 'Рын. капитал')}</span>
                        <span className={s.statValue}>${formatNumber(marketData.marketCap, siteSettings?.numberFormat)}B</span>
                            </div>
                    <div className={s.stat}>
                        <span className={s.statLabel}>{t('tradingDashboard.stats.change24h', 'Изменение 24ч')}</span>
                        <span className={`${s.statValue} ${marketData.priceChange >= 0 ? s.positive : s.negative}`}>
                            {marketData.priceChange >= 0 ? '+' : ''}{((marketData.priceChange / marketData.currentPrice) * 100).toFixed(2)}%
                        </span>
                            </div>
                    <div className={s.stat}>
                        <span className={s.statLabel}>{t('tradingDashboard.stats.indexPrice', 'Индексная цена')}</span>
                        <span className={s.statValue}>${formatNumber(marketData.currentPrice * 1.0001, siteSettings?.numberFormat)}</span>
                    </div>
                </div>

                {/* Убираем дополнительную секцию */}
                            </div>

            {/* Основной торговый интерфейс */}
            <div className={s.tradingInterface}>
                {/* Объединенный блок: График + Order Book на всю ширину */}
                <div className={s.chartAndOrderBook}>
                    {/* График с кастомными данными */}
                    <div className={s.chartSection}>
                        <div className={s.tradingViewChart}>
                            <div ref={chartContainerRef} className={s.chartContainer}></div>
                        </div>
                    </div>

                    {/* Разделитель */}
                    <div className={s.divider}></div>

                    {/* Order Book с анимацией заполнения */}
                    <div className={s.orderBookSection}>
                        <h3 className={s.sectionTitle}>{t('tradingDashboard.sections.orderBook', 'Книга ордеров')}</h3>
                        <div className={s.orderBookHeader}>
                            <span>{t('tradingDashboard.table.price', 'Цена')} (USD)</span>
                            <span>{t('tradingDashboard.table.amount', 'Количество')} ({selectedCrypto || 'BNB'})</span>
                            <span>{t('tradingDashboard.table.amount', 'Количество')}</span>
                        </div>
                        
                        <div className={s.orderBookAsks}>
                            {orderBook.asks.slice(0, 6).reverse().map((ask, index) => (
                                <div key={ask.id} className={`${s.orderBookRow} ${ask.isAnimating ? s.filling : ''}`}>
                                    <div className={s.fillBarAsk} style={{ 
                                        width: `${ask.fillProgress || 0}%`,
                                        opacity: ask.isAnimating ? 1 : 0
                                    }}></div>
                                    <span className={s.sellPrice}>{ask.price}</span>
                                    <span>{ask.amount}</span>
                                    <span>{ask.total}</span>
                                </div>
                            ))}
                        </div>
                        
                        <div className={s.currentPriceRow}>
                            <span className={s.currentPriceLabel}>${marketData.currentPrice.toFixed(2)}</span>
                        </div>
                        
                        <div className={s.orderBookBids}>
                            {orderBook.bids.slice(0, 6).map((bid, index) => (
                                <div key={bid.id} className={`${s.orderBookRow} ${bid.isAnimating ? s.filling : ''}`}>
                                    <div className={s.fillBarBid} style={{ 
                                        width: `${bid.fillProgress || 0}%`,
                                        opacity: bid.isAnimating ? 1 : 0
                                    }}></div>
                                    <span className={s.buyPrice}>{bid.price}</span>
                                    <span>{bid.amount}</span>
                                    <span>{bid.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Нижние блоки: Recent Trades и Trading Panel */}
            <div className={s.bottomInterface}>
                {/* Recent Trades с анимацией новых сделок */}
                <div className={s.historySection}>
                    <h3 className={s.sectionTitle}>{t('tradingDashboard.sections.recentTrades', 'Последние сделки')}</h3>
                    <div className={s.historyTable}>
                        <div className={s.tableHeader}>
                            <span>{t('tradingDashboard.table.time', 'Время')}</span>
                            <span>{t('tradingDashboard.table.price', 'Цена')}</span>
                            <span>{t('tradingDashboard.table.amount', 'Количество')}</span>
                            <span>{t('tradingDashboard.table.side', 'Сторона')}</span>
                        </div>
                        {tradeHistory.slice(0, 10).map((trade) => (
                            <div key={trade.id} className={`${s.tableRow} ${newTrades.has(trade.id) ? s.newTrade : ''}`}>
                                <span>{trade.time}</span>
                                <span className={trade.side === 'buy' ? s.buyText : s.sellText}>
                                    ${formatNumber(trade.price, siteSettings?.numberFormat)}
                                </span>
                                <span>{trade.amount}</span>
                                <span className={trade.side === 'buy' ? s.buyText : s.sellText}>
                                    {trade.side.toUpperCase()}
                                </span>
                    </div>
                        ))}
                    </div>
                </div>

                {/* Trading Panel с реальными данными */}
                <div className={s.tradingPanel}>
                    <div className={s.orderTypeSelector}>
                        <button
                            className={`${s.orderTypeBtn} ${orderType === 'limit' ? s.active : ''}`}
                            onClick={() => setOrderType('limit')}
                        >
                            {t('tradingDashboard.orderTypes.limit', 'Лимит')}
                        </button>
                        <button
                            className={`${s.orderTypeBtn} ${orderType === 'market' ? s.active : ''}`}
                            onClick={() => setOrderType('market')}
                        >
                            {t('tradingDashboard.orderTypes.market', 'Рынок')}
                        </button>
                        <button
                            className={`${s.orderTypeBtn} ${orderType === 'stop' ? s.active : ''}`}
                            onClick={() => setOrderType('stop')}
                        >
                            {t('tradingDashboard.orderTypes.stop', 'Стоп')}
                        </button>
                    </div>

                    <div className={s.orderSideSelector}>
                        <button
                            className={`${s.sideBtn} ${s.buyBtn} ${side === 'buy' ? s.active : ''}`}
                            onClick={() => setSide('buy')}
                        >
                            {t('tradingDashboard.sides.buy', 'Купить')}
                        </button>
                        <button
                            className={`${s.sideBtn} ${s.sellBtn} ${side === 'sell' ? s.active : ''}`}
                            onClick={() => setSide('sell')}
                        >
                            {t('tradingDashboard.sides.sell', 'Продать')}
                        </button>
                        </div>

                    <div className={s.orderForm}>
                        {orderType !== 'market' && (
                            <div className={s.inputGroup}>
                                <label>{t('tradingDashboard.form.priceLabel', 'Цена (USD)')}</label>
                                <input
                                    type="number"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    className={s.orderInput}
                                    step="0.01"
                                    placeholder={t('tradingDashboard.form.pricePlaceholder', '113,418.97')}
                                />
                            </div>
                        )}
                        
                        <div className={s.inputGroup}>
                            <label>{t('tradingDashboard.form.amountLabel', 'Количество')} ({selectedCrypto || 'BNB'})</label>
                                <input
                                type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                placeholder={t('tradingDashboard.form.amountPlaceholder', '0.00')}
                                className={s.orderInput}
                                step="0.0001"
                            />
                        </div>

                        <div className={s.percentageButtons}>
                            {['25%', '50%', '75%', '100%'].map(percent => (
                                <button 
                                    key={percent} 
                                    className={s.percentBtn}
                                    onClick={() => {
                                        const balance = maxTotalMoney;
                                        const priceValue = parseFloat(price) || marketData.currentPrice;
                                        const percentage = parseInt(percent) / 100;
                                        const calculatedAmount = (balance * percentage / priceValue).toFixed(4);
                                        setAmount(calculatedAmount);
                                    }}
                                >
                                    {percent}
                                    </button>
                                        ))}
                                    </div>

                        <div className={s.orderSummary}>
                            <div className={s.summaryRow}>
                                <span>{t('tradingDashboard.summary.available', 'Доступно')}</span>
                                <span>{currencyFormat || 'RM'} {formatNumber(maxTotalMoney, siteSettings?.numberFormat)}</span>
                            </div>
                            <div className={s.summaryRow}>
                                <span>{t('tradingDashboard.summary.total', 'Итого')}</span>
                                <span>${formatNumber((parseFloat(price) || marketData.currentPrice) * parseFloat(amount || 0), siteSettings?.numberFormat)}</span>
                                </div>
                            <div className={s.summaryRow}>
                                <span>{t('tradingDashboard.summary.fee', 'Комиссия (0.1%)')}</span>
                                <span>${formatNumber(((parseFloat(price) || marketData.currentPrice) * parseFloat(amount || 0)) * 0.001, siteSettings?.numberFormat)}</span>
                            </div>
                        </div>

                        <button
                            className={`${s.placeOrderBtn} ${side === 'buy' ? s.buyOrder : s.sellOrder}`}
                            onClick={handlePlaceOrder}
                            disabled={!amount || (orderType !== 'market' && !price)}
                        >
                            {side === 'buy' ? t('tradingDashboard.sides.buy', 'Купить') : t('tradingDashboard.sides.sell', 'Продать')} {selectedCrypto || 'BNB'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Нижняя секция - Open Orders на всю ширину */}
            <div className={s.bottomSection}>
                <div className={s.ordersSection}>
                    <h3 className={s.sectionTitle}>{t('tradingDashboard.sections.openOrders', 'Открытые ордера')}</h3>
                    <div className={s.ordersTable}>
                        <div className={s.tableHeader}>
                            <span>{t('tradingDashboard.table.type', 'Тип')}</span>
                            <span>{t('tradingDashboard.table.pair', 'Пара')}</span>
                            <span>{t('tradingDashboard.table.price', 'Цена')}</span>
                            <span>{t('tradingDashboard.table.amount', 'Количество')}</span>
                            <span>{t('tradingDashboard.table.filled', 'Исполнено')}</span>
                            <span>{t('tradingDashboard.table.time', 'Время')}</span>
                            <span>{t('tradingDashboard.table.action', 'Действие')}</span>
                        </div>
                        {openOrders.length > 0 ? (
                            openOrders.map(order => (
                                <div key={order.id} className={s.tableRow}>
                                    <span className={order.type === 'Buy' ? s.buyText : s.sellText}>
                                        {order.type}
                                    </span>
                                    <span>{order.pair}</span>
                                    <span>${formatNumber(order.price, siteSettings?.numberFormat)}</span>
                                    <span>{order.amount}</span>
                                    <span>{order.filled.toFixed(1)}%</span>
                                    <span>{order.time}</span>
                                    <span>
                                        <button className={s.cancelBtn}>{t('tradingDashboard.buttons.cancel', 'Отменить')}</button>
                                    </span>
                        </div>
                            ))
                        ) : (
                            <div className={s.emptyState}>
                                <p>{t('tradingDashboard.emptyState.noOpenOrders', 'Нет открытых ордеров')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};